import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, Table, Thead, Tbody, Tr, Th, Td,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, ModalFooter, FormControl, FormLabel, Input, useToast,
  Badge, Spinner, IconButton, Tooltip, Checkbox, CheckboxGroup,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import api from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import { Role } from '../types';

interface RoleData extends Role {
  users?: { id: number; username: string; full_name: string }[];
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<RoleData[]>([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editItem, setEditItem] = useState<RoleData | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRole, setAssignRole] = useState<RoleData | null>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const toast = useToast();
  const { t } = useLanguage();

  const DEFAULT_ROLES = ['admin', 'manager', 'users', 'guest'];

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes] = await Promise.allSettled([
        api.get('/api/roles/').then(r => r.data),
        api.get('/api/users/').then(r => r.data),
      ]);
      if (rolesRes.status === 'fulfilled') setRoles(rolesRes.value);
      if (usersRes.status === 'fulfilled') setAllUsers(usersRes.value);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditItem(null);
    setFormData({ name: '' });
    onOpen();
  };

  const handleEdit = (item: RoleData) => {
    setEditItem(item);
    setFormData({ name: item.name });
    onOpen();
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: t('roles.fillRequired'), status: 'warning' });
      return;
    }
    try {
      if (editItem) {
        await api.put(`/api/roles/${editItem.id}`, formData);
        toast({ title: t('roles.updatedSuccess'), status: 'success' });
      } else {
        await api.post('/api/roles/', formData);
        toast({ title: t('roles.createdSuccess'), status: 'success' });
      }
      fetchData();
      onClose();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('roles.saveError'), status: 'error' });
    }
  };

  const handleDelete = async (roleId: number) => {
    if (!window.confirm(t('roles.confirmDelete'))) return;
    try {
      await api.delete(`/api/roles/${roleId}`);
      toast({ title: t('roles.deletedSuccess'), status: 'success' });
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('roles.deleteError'), status: 'error' });
    }
  };

  const handleOpenAssign = (role: RoleData) => {
    setAssignRole(role);
    setSelectedUsers((role.users || []).map(u => u.id));
    setAssignOpen(true);
  };

  const handleSaveAssign = async () => {
    if (!assignRole) return;
    try {
      const currentIds = (assignRole.users || []).map(u => u.id);
      const toAdd = selectedUsers.filter(id => !currentIds.includes(id));
      const toRemove = currentIds.filter(id => !selectedUsers.includes(id));
      await Promise.all([
        ...toAdd.map(userId => api.post(`/api/roles/${assignRole.id}/users/${userId}`)),
        ...toRemove.map(userId => api.delete(`/api/roles/${assignRole.id}/users/${userId}`)),
      ]);
      toast({ title: t('roles.assignSuccess'), status: 'success' });
      fetchData();
      setAssignOpen(false);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('roles.assignError'), status: 'error' });
    }
  };

  const isDefaultRole = (name: string) => DEFAULT_ROLES.includes(name);

  if (loading) return <Spinner size="xl" display="block" mx="auto" mt={10} />;

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <HStack justifyContent="space-between" flexWrap="wrap">
          <Heading size="lg" color="blue.600">{t('roles.title')}</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={handleCreate}>{t('roles.addRole')}</Button>
        </HStack>

        <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
          <Table variant="simple" size="sm">
            <Thead bg="#0F2C59">
              <Tr>
                <Th color="white">ID</Th>
                <Th color="white">{t('roles.name')}</Th>
                <Th color="white">{t('roles.users')}</Th>
                <Th color="white">{t('common.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {roles.map((role) => (
                <Tr key={role.id} _hover={{ bg: 'gray.50' }}>
                  <Td>{role.id}</Td>
                  <Td>
                    <Badge colorScheme={isDefaultRole(role.name) ? 'blue' : 'gray'} borderRadius="full">
                      {role.name}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {(role.users || []).slice(0, 3).map(u => (
                        <Badge key={u.id} colorScheme="green" fontSize="xs">{u.username}</Badge>
                      ))}
                      {(role.users || []).length > 3 && (
                        <Badge colorScheme="gray" fontSize="xs">+{(role.users || []).length - 3}</Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label={t('roles.assignUsers')}>
                        <IconButton aria-label={t('roles.assignUsers')} icon={<RepeatIcon />} size="xs" colorScheme="teal" variant="ghost" onClick={() => handleOpenAssign(role)} />
                      </Tooltip>
                      {!isDefaultRole(role.name) && (
                        <>
                          <Tooltip label={t('common.edit')}>
                            <IconButton aria-label={t('common.edit')} icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={() => handleEdit(role)} />
                          </Tooltip>
                          <Tooltip label={t('common.delete')}>
                            <IconButton aria-label={t('common.delete')} icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleDelete(role.id)} />
                          </Tooltip>
                        </>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </VStack>

      {/* Create/Edit Role Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? t('roles.editTitle') : t('roles.addTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{t('roles.name')}</FormLabel>
              <Input value={formData.name} onChange={(e) => setFormData({ name: e.target.value })} placeholder={t('roles.namePlaceholder')} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Users Modal */}
      <Modal isOpen={assignOpen} onClose={() => setAssignOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('roles.assignTitle', { role: assignRole?.name ?? '' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
              {allUsers.map((user: any) => (
                <HStack key={user.id} justifyContent="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                  <VStack spacing={0} align="flex-start">
                    <Text fontWeight="bold" fontSize="sm">{user.full_name}</Text>
                    <Text fontSize="xs" color="gray.500">{user.username} ({user.email})</Text>
                  </VStack>
                  <Checkbox
                    isChecked={selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedUsers(prev => [...prev, user.id]);
                      else setSelectedUsers(prev => prev.filter(id => id !== user.id));
                    }}
                  />
                </HStack>
              ))}
              {allUsers.length === 0 && <Text color="gray.500">{t('roles.noUsers')}</Text>}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setAssignOpen(false)}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSaveAssign}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RoleManagement;
